import React from "react";
import { ArrowRight, ShieldCheck, Ship, Globe2 } from "lucide-react";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden bg-navy"
    >
      {/* Background image with dark overlay */}
      <div className="absolute inset-0 -z-0">
        <img
          src="https://images.unsplash.com/photo-1494412651409-8963ce7935a7?q=80&w=2400&auto=format&fit=crop"
          alt="Container ship at global port"
          className="w-full h-full object-cover"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(115deg, rgba(11,29,58,0.92) 0%, rgba(11,29,58,0.78) 45%, rgba(0,94,68,0.55) 100%)",
          }}
        />
        <div className="absolute inset-0 grid-overlay opacity-30" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 text-white">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm mb-6">
              <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full pulse-dot" />
              <span className="text-xs tracking-[0.2em] uppercase font-semibold text-white/90">
                Indian FMCG Merchant Exporter
              </span>
            </div>

            <h1
              className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.05] font-bold tracking-tight"
              data-testid="hero-heading"
            >
              Export Authentic <span className="text-gold italic">Indian FMCG</span> Brands Worldwide
            </h1>

            <p className="mt-6 text-lg lg:text-xl text-white/85 leading-relaxed max-w-2xl">
              We help importers, wholesalers, supermarket chains and Indian
              grocery retailers source authentic Indian branded FMCG products
              through reliable procurement, competitive pricing and professional
              export solutions.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <a href="#contact" data-testid="hero-request-quote" className="btn-primary bg-white text-navy hover:bg-white/90">
                Request a Quote <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="#products"
                data-testid="hero-browse-products"
                className="btn-secondary bg-transparent border-white text-white hover:bg-white hover:text-navy"
              >
                Browse Products
              </a>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
              {[
                { n: "18+", l: "Global Markets" },
                { n: "50+", l: "Brand SKUs" },
                { n: "100%", l: "Authentic Sourcing" },
              ].map((s) => (
                <div key={s.l} className="border-l border-white/25 pl-4">
                  <div className="font-display text-3xl lg:text-4xl text-white font-bold">
                    {s.n}
                  </div>
                  <div className="text-xs tracking-widest uppercase text-white/60 mt-1">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="glass rounded-3xl p-6 lg:p-8 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-navy text-white flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs tracking-widest uppercase text-emerald font-semibold">
                    Why Babul
                  </div>
                  <div className="font-display text-xl text-navy font-bold">
                    Built for reliable global sourcing
                  </div>
                </div>
              </div>

              {[
                { icon: Ship, t: "Mixed Container Shipments", d: "Multiple brands, one container." },
                { icon: ShieldCheck, t: "Authorized-Channel Sourcing", d: "100% genuine branded stock." },
                { icon: Globe2, t: "Serving 18+ Countries", d: "USA · UK · GCC · Australia · APAC · Africa." },
              ].map((f, i) => (
                <div
                  key={i}
                  className="flex items-start gap-4 p-4 rounded-2xl bg-white/70 border border-white/60"
                >
                  <div className="w-10 h-10 rounded-lg bg-emerald/10 text-emerald flex items-center justify-center shrink-0">
                    <f.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-semibold text-navy">{f.t}</div>
                    <div className="text-sm text-slate-600 mt-0.5">{f.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
